// Reads a sentence typed into the home search bar, like
// "this sunday picnic in the park with friends at 12:00", and pulls out the
// date, time and (when clearly named) the place. Whatever is left becomes the
// title. Works offline, English plus common German words. Dates follow the
// German order (18.10. / 18/10 = 18 October).

const WEEKDAYS = [
  ['sunday', 'sun', 'sonntag', 'so'],
  ['monday', 'mon', 'montag', 'mo'],
  ['tuesday', 'tues', 'tue', 'dienstag', 'di'],
  ['wednesday', 'wed', 'mittwoch', 'mi'],
  ['thursday', 'thurs', 'thu', 'donnerstag', 'do'],
  ['friday', 'fri', 'freitag', 'fr'],
  ['saturday', 'sat', 'samstag', 'sa'],
];

const MONTHS = [
  ['january', 'jan', 'januar', 'jänner'],
  ['february', 'feb', 'februar'],
  ['march', 'mar', 'märz', 'maerz', 'mär'],
  ['april', 'apr'],
  ['may', 'mai'],
  ['june', 'jun', 'juni'],
  ['july', 'jul', 'juli'],
  ['august', 'aug'],
  ['september', 'sept', 'sep'],
  ['october', 'oct', 'oktober', 'okt'],
  ['november', 'nov'],
  ['december', 'dec', 'dezember', 'dez'],
];

// Two-letter German weekday abbreviations ("so", "do") are real words, so they
// only count after "am"/"this"/"next"/"on".
const SHORT_GERMAN = new Set(['so', 'mo', 'di', 'mi', 'do', 'fr', 'sa']);

const weekdayIndex = (word) => WEEKDAYS.findIndex((names) => names.includes(word));
const monthIndex = (word) => MONTHS.findIndex((names) => names.includes(word.replace(/\.$/, '')));

const weekdayWords = WEEKDAYS.flat().filter((word) => !SHORT_GERMAN.has(word)).join('|');
const allWeekdayWords = WEEKDAYS.flat().join('|');
const monthWords = MONTHS.flat().sort((a, b) => b.length - a.length).join('|');

const pad = (value) => String(value).padStart(2, '0');
const toISODate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

// A day/month without a year means the next time that date comes round.
function upcomingDate(day, month, year, today) {
  if (year) {
    const full = year < 100 ? 2000 + year : year;
    const date = new Date(full, month, day);
    return date.getMonth() === month ? date : null;
  }
  let date = new Date(today.getFullYear(), month, day);
  if (date.getMonth() !== month) return null;
  if (date < today) date = new Date(today.getFullYear() + 1, month, day);
  return date;
}

function nextWeekday(target, today, { allowToday = true } = {}) {
  let days = (target - today.getDay() + 7) % 7;
  if (days === 0 && !allowToday) days = 7;
  return addDays(today, days);
}

// Each rule finds one thing; its matched text is cut out of the title.
const DATE_RULES = [
  {
    pattern: /\b(?:the\s+)?day\s+after\s+tomorrow\b|\bübermorgen\b/i,
    read: (match, today) => addDays(today, 2),
  },
  {
    pattern: /\b(?:today|tonight|this\s+evening|heute(?:\s+abend)?)\b/i,
    read: (match, today) => today,
  },
  {
    pattern: /\b(?:tomorrow|morgen)\b/i,
    read: (match, today) => addDays(today, 1),
  },
  {
    pattern: /\b(?:this\s+)?(?:weekend|wochenende)\b/i,
    read: (match, today) => nextWeekday(6, today),
  },
  {
    // "this sunday", "next friday", "on sat", "am Sonntag", "sunday"
    pattern: new RegExp(`\\b(?:(this|next|coming|on|am|nächsten|naechsten|kommenden)\\s+)?(${weekdayWords})\\b`, 'i'),
    read: (match, today) => {
      const qualifier = (match[1] || '').toLowerCase();
      const target = weekdayIndex(match[2].toLowerCase());
      return nextWeekday(target, today, { allowToday: !['next', 'nächsten', 'naechsten', 'kommenden'].includes(qualifier) });
    },
  },
  {
    // German two-letter days only with a lead-in: "am So", "this Sa"
    pattern: new RegExp(`\\b(this|next|on|am)\\s+(${allWeekdayWords})\\.?(?=\\s|$)`, 'i'),
    read: (match, today) => {
      const target = weekdayIndex(match[2].toLowerCase());
      return target < 0 ? null : nextWeekday(target, today, { allowToday: match[1].toLowerCase() !== 'next' });
    },
  },
  {
    // 18.10.2026, 18.10., 18/10/26, 18/10
    pattern: /\b(?:on\s+|am\s+)?(\d{1,2})[./](\d{1,2})(?:[./](\d{2}|\d{4})\b|\.(?!\d))(?!:)|\b(?:on\s+|am\s+)?(\d{1,2})\/(\d{1,2})\b(?!:)/i,
    read: (match, today) => {
      const day = Number(match[1] || match[4]);
      const month = Number(match[2] || match[5]) - 1;
      const year = match[3] ? Number(match[3]) : 0;
      return month >= 0 && month < 12 ? upcomingDate(day, month, year, today) : null;
    },
  },
  {
    // 18 october, 18th of oct, 18. Oktober 2026
    pattern: new RegExp(`\\b(?:on\\s+|am\\s+)?(\\d{1,2})(?:st|nd|rd|th|\\.)?\\s+(?:of\\s+)?(${monthWords})\\.?(?:\\s+(\\d{4}))?\\b`, 'i'),
    read: (match, today) => upcomingDate(Number(match[1]), monthIndex(match[2].toLowerCase()), Number(match[3] || 0), today),
  },
  {
    // october 18, oct 18th 2026
    pattern: new RegExp(`\\b(?:on\\s+)?(${monthWords})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?\\b`, 'i'),
    read: (match, today) => upcomingDate(Number(match[2]), monthIndex(match[1].toLowerCase()), Number(match[3] || 0), today),
  },
  {
    pattern: /\bin\s+(\d{1,2})\s+(?:days|tagen)\b/i,
    read: (match, today) => addDays(today, Number(match[1])),
  },
];

// One clock time inside a range: 11, 11am, 11:30, 16.30 uhr, 19h.
const CLOCK = '(\\d{1,2})(?:[:.](\\d{2}))?\\s*(am|pm|a\\.m\\.|p\\.m\\.|uhr|h)?';
const RANGE_JOIN = '\\s*(?:-|–|—|to|till|til|until|bis|and|und)\\s*';

// "from 11am to 4pm", "11-4pm", "7–10pm", "18:00 - 22:00",
// "between 7 and 10pm", "von 18 bis 22 Uhr".
const RANGE_PATTERN = new RegExp(
  `(?:\\b(from|between|von|ab|at|um)\\s+|@\\s*|\\b)${CLOCK}${RANGE_JOIN}${CLOCK}(?![\\w.]*\\d)`,
  'i'
);

// An end on its own, after a start was found: "until 4pm", "till 23:00", "bis 22 Uhr".
const END_PATTERN = new RegExp(`\\b(?:until|till|til|to|bis|ends?(?:\\s+at)?)\\s+${CLOCK}(?![\\w.]*\\d)`, 'i');

const marker = (value) => String(value || '').toLowerCase().replace(/\./g, '');
const isMeridiem = (value) => ['am', 'pm'].includes(marker(value));

function readRange(match) {
  const [, lead, h1, m1, s1, h2, m2, s2] = match;
  const startHour = Number(h1);
  const endHour = Number(h2);
  // A range needs something that says "time": am/pm/uhr, minutes, or a
  // lead-in word. Otherwise "1-2" or a postcode isn't read as a time.
  if (!(s1 || s2 || m1 || m2 || lead)) return null;

  let endSuffix = s2;
  let startSuffix = s1;
  // "11-4pm": the start borrows pm only when that keeps it before the end
  // ("7-10pm" is 19:00-22:00, "11-4pm" is 11:00-16:00).
  if (!startSuffix && isMeridiem(endSuffix) && !m1 && startHour <= 12) {
    const asPm = to24h(startHour, 0, endSuffix);
    const endAt = to24h(endHour, Number(m2 || 0), endSuffix);
    startSuffix = asPm && endAt && asPm[0] * 60 < endAt[0] * 60 + endAt[1] ? endSuffix : 'am';
  }
  if (!endSuffix && isMeridiem(startSuffix) && !m2 && endHour <= 12) endSuffix = startSuffix;

  let start = to24h(startHour, Number(m1 || 0), startSuffix);
  let end = to24h(endHour, Number(m2 || 0), endSuffix);
  if (!start || !end) return null;

  // Bare hours ("from 7 to 10"): evening, like "at 7".
  if (!startSuffix && !m1 && start[0] >= 1 && start[0] <= 7) start = [start[0] + 12, start[1]];
  if (!endSuffix && !m2 && end[0] < 12 && end[0] * 60 + end[1] <= start[0] * 60 + start[1] && end[0] + 12 > start[0]) {
    end = [end[0] + 12, end[1]];
  }
  return [start, end];
}

const TIME_RULES = [
  {
    pattern: /\b(?:at\s+|@\s*|um\s+|from\s+|ab\s+)?(noon|midday|mittag)\b/i,
    read: () => [12, 0],
  },
  {
    pattern: /\b(?:at\s+|@\s*|um\s+)?(midnight|mitternacht)\b/i,
    read: () => [0, 0],
  },
  {
    // 12:00, 7:30pm, 19.30 uhr, 9.15am
    pattern: /(?:\b(?:at|um|from|ab)\s+|@\s*|\b)(\d{1,2})(?::(\d{2})|\.(\d{2})(?=\s*(?:am|pm|uhr)\b))\s*(am|pm|a\.m\.|p\.m\.|uhr|h)?(?![\w.]*\d)/i,
    read: (match) => to24h(Number(match[1]), Number(match[2] || match[3]), match[4]),
  },
  {
    // 7pm, 11 am, 19 uhr, 19h
    pattern: /(?:\b(?:at|um|from|ab)\s+|@\s*|\b)(\d{1,2})\s*(am|pm|a\.m\.|p\.m\.|uhr|h)\b/i,
    read: (match) => to24h(Number(match[1]), 0, match[2]),
  },
  {
    // "at 7": a bare hour after "at"/"um"/"@". 1–7 reads as afternoon/evening.
    pattern: /(?:\b(?:at|um)\s+|@\s*)(\d{1,2})\b(?![.:/]\d)/i,
    read: (match) => {
      const hour = Number(match[1]);
      if (hour > 23) return null;
      return [hour >= 1 && hour <= 7 ? hour + 12 : hour, 0];
    },
  },
];

function to24h(hour, minute, suffix = '') {
  const marker = String(suffix || '').toLowerCase().replace(/\./g, '');
  if (minute > 59) return null;
  if (marker === 'pm' && hour < 12) hour += 12;
  else if (marker === 'am' && hour === 12) hour = 0;
  if (hour > 23) return null;
  return [hour, minute];
}

// A clearly named place: "at Tempelhofer Feld", "in Berlin", "@ Café Luna".
// Needs a capital letter, so "in the park" stays part of the title.
const PLACE_WORD = "[A-ZÄÖÜ][\\wÄÖÜäöüßéèàç'’&-]*";
const PLACE_JOINER = '(?:der|die|das|des|dem|am|an|im|de|la|le|von|van|the|of|and|&)';
const PLACE_PATTERN = new RegExp(
  `(?:\\b(?:at|in|im|bei)\\s+|@\\s*)(${PLACE_WORD}(?:\\s+(?:${PLACE_WORD}|${PLACE_JOINER}(?=\\s+[A-ZÄÖÜ])))*)`
);
const NOT_A_PLACE = new RegExp(`^(?:${allWeekdayWords}|${monthWords}|noon|midnight|the|a|an)$`, 'i');

// Lead-in words left dangling once a date or time has been cut out.
const DANGLING = /(?:^|\s)(?:this|next|coming|on|at|um|am|from|ab|@|,|-|–)(?=\s*(?:$|[,.!?]))/gi;

function cut(text, start, length) {
  return `${text.slice(0, start)} ${text.slice(start + length)}`;
}

export function parseEventText(input, now = new Date()) {
  const original = String(input || '').trim();
  const today = startOfDay(now);
  let text = ` ${original} `;
  let date = '';
  let time = '';
  let endTime = '';
  let location = '';

  for (const rule of DATE_RULES) {
    const match = rule.pattern.exec(text);
    if (!match) continue;
    const value = rule.read(match, today);
    if (!value) continue;
    date = toISODate(value);
    text = cut(text, match.index, match[0].length);
    break;
  }

  const range = RANGE_PATTERN.exec(text);
  const rangeValue = range && readRange(range);
  if (rangeValue) {
    time = `${pad(rangeValue[0][0])}:${pad(rangeValue[0][1])}`;
    endTime = `${pad(rangeValue[1][0])}:${pad(rangeValue[1][1])}`;
    text = cut(text, range.index, range[0].length);
  } else {
    for (const rule of TIME_RULES) {
      const match = rule.pattern.exec(text);
      if (!match) continue;
      const value = rule.read(match);
      if (!value) continue;
      time = `${pad(value[0])}:${pad(value[1])}`;
      text = cut(text, match.index, match[0].length);
      break;
    }

    const endMatch = time ? END_PATTERN.exec(text) : null;
    if (endMatch) {
      const [startHour] = time.split(':').map(Number);
      let value = to24h(Number(endMatch[1]), Number(endMatch[2] || 0), endMatch[3]);
      // "until 4" after a 2pm start means 16:00.
      if (value && !endMatch[3] && !endMatch[2] && value[0] < 12 && value[0] + 12 > startHour && value[0] <= startHour) {
        value = [value[0] + 12, value[1]];
      }
      if (value) {
        endTime = `${pad(value[0])}:${pad(value[1])}`;
        text = cut(text, endMatch.index, endMatch[0].length);
      }
    }
  }

  const place = PLACE_PATTERN.exec(text);
  if (place && !NOT_A_PLACE.test(place[1].split(/\s+/)[0])) {
    location = place[1].replace(/\s+(?:and|&|of|the)$/i, '').trim();
    text = cut(text, place.index, place[0].length);
  }

  let title = text.replace(DANGLING, ' ');
  title = title.replace(/\s+([,.!?])/g, '$1').replace(/\s{2,}/g, ' ').trim();
  title = title.replace(/^[,.\-–:;!?\s]+|[,\-–:;\s]+$/g, '').trim();
  if (title) title = title.charAt(0).toUpperCase() + title.slice(1);

  return {
    title,
    date,
    time,
    endTime,
    location,
    // Only treat it as an event when a date or time was found, so ordinary
    // searches ("picnic", "berlin") keep working as searches.
    isEvent: Boolean(title && (date || time)),
  };
}

// "Sun 18.10.2026 · 12:00" for the hint under the search bar.
export function describeParsedEvent({ date, time, endTime, location }) {
  const parts = [];
  if (date) {
    const [year, month, day] = date.split('-').map(Number);
    const weekday = new Date(year, month - 1, day).toLocaleDateString('en-GB', { weekday: 'short' });
    parts.push(`${weekday} ${pad(day)}.${pad(month)}.${year}`);
  }
  if (time) parts.push(endTime ? `${time}–${endTime}` : time);
  if (location) parts.push(location);
  return parts.join(' · ');
}
